import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Check, X as XIcon, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { cn } from '../utils/cn';

interface ExtractedKeyword {
  term: string;
  definition: string;
  category: string;
  aliases: string[];
  tags: string[];
  selected?: boolean;
}

interface GlossaryExtractionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutorial: {
    title: string;
    description?: string;
    steps: Array<{ title: string; content: string; subsections?: Array<{ content: string }> }>;
  };
}

const GlossaryExtractionModal: React.FC<GlossaryExtractionModalProps> = ({
  isOpen,
  onClose,
  tutorial
}) => {
  const [keywords, setKeywords] = useState<ExtractedKeyword[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);

  // Debug logging
  useEffect(() => {
    console.log('GlossaryExtractionModal isOpen:', isOpen, 'tutorial:', tutorial);
  }, [isOpen, tutorial]);

  useEffect(() => {
    if (isOpen && keywords.length === 0 && !isExtracting) {
      handleExtract();
    }
  }, [isOpen]);
  
  // Reset keywords when modal closes
  useEffect(() => {
    if (!isOpen) {
      setKeywords([]);
      setIsExtracting(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const count = keywords.filter(k => k.selected !== false).length;
    setSelectedCount(count);
  }, [keywords]);

  const handleExtract = async () => {
    console.log('handleExtract called', { 
      title: tutorial.title, 
      stepsCount: tutorial.steps?.length,
      steps: tutorial.steps 
    });
    
    if (!tutorial.title || !tutorial.steps || tutorial.steps.length === 0) {
      console.error('Validation failed:', { 
        hasTitle: !!tutorial.title, 
        hasSteps: !!tutorial.steps, 
        stepsLength: tutorial.steps?.length 
      });
      toast.error('Tutorial must have a title and at least one step');
      return;
    }

    setIsExtracting(true);
    try {
      console.log('Calling API to extract terms...');
      const response = await apiService.extractGlossaryTerms(tutorial);
      console.log('Extract response:', response);
      
      // Check for keywords in both response.data.keywords and response.keywords (depending on API structure)
      const keywords = response.data?.keywords || (response as any).keywords;
      
      if (response.success && keywords && Array.isArray(keywords) && keywords.length > 0) {
        const keywordsWithSelection = keywords.map((k: ExtractedKeyword) => ({
          ...k,
          selected: true // Default to selected
        }));
        console.log('Setting keywords:', keywordsWithSelection);
        setKeywords(keywordsWithSelection);
        toast.success(`Extracted ${keywordsWithSelection.length} technical terms`);
      } else {
        console.error('Extract failed or no keywords:', { 
          success: response.success, 
          hasData: !!response.data, 
          hasKeywords: !!keywords,
          keywordsLength: keywords?.length 
        });
        toast.error('Failed to extract terms or no terms found');
      }
    } catch (error: any) {
      console.error('Extract terms error:', error);
      toast.error(error.message || 'Failed to extract terms');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleToggleKeyword = (index: number) => {
    setKeywords(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected: !updated[index].selected };
      return updated;
    });
  };

  const handleSubmit = async () => {
    const selectedKeywords = keywords.filter(k => k.selected !== false);
    
    if (selectedKeywords.length === 0) {
      toast.error('Please select at least one term to submit');
      return;
    }

    setIsSubmitting(true);
    try {
      const entries = selectedKeywords.map(k => ({
        term: k.term,
        definition: k.definition,
        category: k.category,
        aliases: k.aliases || [],
        tags: k.tags || []
      }));

      const response = await apiService.submitGlossaryEntries(entries);
      if (response.success) {
        toast.success(`Submitted ${selectedKeywords.length} term${selectedKeywords.length === 1 ? '' : 's'} for approval`);
        setKeywords([]);
        onClose();
        // Trigger a refresh of the glossary management page if it's open
        // This is a workaround - ideally the parent component would handle this
        window.dispatchEvent(new CustomEvent('glossaryUpdated'));
      } else {
        toast.error('Failed to submit terms');
      }
    } catch (error: any) {
      console.error('Submit terms error:', error);
      toast.error(error.message || 'Failed to submit terms');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectAll = () => {
    setKeywords(prev => prev.map(k => ({ ...k, selected: true })));
  };

  const handleDeselectAll = () => {
    setKeywords(prev => prev.map(k => ({ ...k, selected: false })));
  };

  console.log('GlossaryExtractionModal render - isOpen:', isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={onClose}
          style={{ zIndex: 10000 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
          >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Extract Technical Terms</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isExtracting ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin mb-4" />
                <p className="text-gray-600">Extracting technical terms from your tutorial...</p>
              </div>
            ) : keywords.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Sparkles className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">No terms extracted yet</p>
                <button
                  onClick={handleExtract}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Extract Terms
                </button>
              </div>
            ) : (
              <>
                {/* Actions */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {selectedCount} of {keywords.length} selected
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-purple-600 hover:text-purple-700"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={handleDeselectAll}
                      className="text-sm text-gray-600 hover:text-gray-700"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                {/* Keywords List */}
                <div className="space-y-3">
                  {keywords.map((keyword, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      onClick={() => handleToggleKeyword(index)}
                      className={cn(
                        "border rounded-lg p-4 transition-all cursor-pointer hover:shadow-md",
                        keyword.selected !== false
                          ? "border-purple-200 bg-purple-50"
                          : "border-gray-200 bg-white hover:border-purple-300"
                      )}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className={cn(
                            "mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0",
                            keyword.selected !== false
                              ? "bg-purple-600 border-purple-600"
                              : "border-gray-300"
                          )}
                        >
                          {keyword.selected !== false && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-900">{keyword.term}</h3>
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                              {keyword.category}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{keyword.definition}</p>
                          {keyword.aliases && keyword.aliases.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              <span className="text-xs text-gray-500">Aliases:</span>
                              {keyword.aliases.map((alias, i) => (
                                <span key={i} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                  {alias}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <div className="flex items-center space-x-3">
              {keywords.length > 0 && (
                <button
                  onClick={handleExtract}
                  disabled={isExtracting}
                  className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  Re-extract
                </button>
              )}
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || selectedCount === 0}
                className={cn(
                  "px-6 py-2 rounded-lg transition-colors flex items-center space-x-2",
                  selectedCount > 0
                    ? "bg-purple-600 text-white hover:bg-purple-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit {selectedCount > 0 ? `${selectedCount} ` : ''}Term{selectedCount !== 1 ? 's' : ''} for Approval</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlossaryExtractionModal;

